import type {RegistrationFlowConfig} from './registrationFlow';
import {
	buildStatusConnectionIntroCredentials,
	isFinanceSelfEmploymentFlow,
	requiresMyTaxConnectionFlow,
} from './statusConnectionFlow';
import {RegistrationWorkStatus} from './types';

export type PostInnStepLink = {
	label: string;
	url: string;
};

export type PostInnIntroSecondaryAction =
	| {kind: 'howToBecome'; label: string}
	/** Альфа «Приступим?»: открыть модалку «Для работы требуется…». */
	| {kind: 'confirmInfo'; label: string};

export type PostInnIntroConfirm = {
	title: string;
	description: string;
};

export type PostInnIntroConfig = {
	title: string;
	description: string;
	secondaryAction: PostInnIntroSecondaryAction | null;
	proceedLabel: string;
	confirm?: PostInnIntroConfirm;
};

export type PostInnAccessConfig = {
	title: string;
	description: string;
	primaryAction: PostInnStepLink & {mobileLabel?: string};
	confirmLabel: string;
};

export type PostInnFlowConfig = {
	intro: PostInnIntroConfig;
	access: PostInnAccessConfig | null;
};

const MY_TAX_APP_URL = 'https://lknpd.nalog.ru/';

const MY_TAX_ACCESS_PRIMARY_ACTION = {
	label: 'Перейти на сайт «Мой налог»',
	mobileLabel: 'Перейти в «Мой налог»',
	url: MY_TAX_APP_URL,
} as const;

/** Обычный СМЗ: партнёр «Рокет Ворк». */
const MY_TAX_ACCESS: PostInnAccessConfig = {
	title: 'Нужен доступ в приложении «Мой налог»',
	description:
		'Найдите Рокет Ворк в разделе «Партнеры» в приложении «Мой налог» или на сайте и предоставьте доступ',
	primaryAction: MY_TAX_ACCESS_PRIMARY_ACTION,
	confirmLabel: 'Доступ предоставлен',
};

/** Альфа / finance-ypla: партнёр «Альфа-банк». */
const ALFA_MY_TAX_ACCESS: PostInnAccessConfig = {
	title: 'Нужен доступ в приложении «Мой налог»',
	description:
		'Найдите Альфа-банк в разделе «Партнеры» в приложении «Мой налог» или на сайте и предоставьте доступ',
	primaryAction: MY_TAX_ACCESS_PRIMARY_ACTION,
	confirmLabel: 'Доступ предоставлен',
};

export type PostInnFlowOptions = {
	snilsRequired?: boolean;
	registrationFlow?: RegistrationFlowConfig;
	/** `free_smz` — экран «Подключить?» с ИНН, без анкеты Альфа, СНИЛС и доступа в «Мой налог». */
	selfEmploymentProvider?: string | null;
};

const FREE_SMZ_INTRO_DESCRIPTION =
	'Нужно проверить ваш статус самозанятого. Если вы ещё не самозанятый, то сперва вам надо зарегистрироваться в «Моём налоге»';

const FALLBACK_REGISTRATION_FLOW: RegistrationFlowConfig = {
	allowedWorkStatuses: ['individual'],
	taxIdType: 'russian_inn',
	snils: 'mandatory',
	residentCode: 'optional',
	paymentMethods: [],
};

const buildIntroDescription = (
	workStatus: RegistrationWorkStatus,
	registrationFlow: RegistrationFlowConfig,
): string => {
	const credentials = buildStatusConnectionIntroCredentials(workStatus, registrationFlow);
	const base = credentials
		? `Для подключения статуса нужно ввести: ${credentials}`
		: 'Для подключения статуса подтвердите данные';

	if (requiresMyTaxConnectionFlow(workStatus)) {
		return `${base}. Затем зарегистрироваться в приложении «Мой налог» и предоставить доступ нашему приложению`;
	}

	return base;
};

const buildIntroSecondaryAction = (
	workStatus: RegistrationWorkStatus,
): PostInnIntroSecondaryAction => {
	if (workStatus === 'individual') {
		return {kind: 'howToBecome', label: 'Как стать физическим лицом'};
	}

	if (workStatus === 'individual_entrepreneur') {
		return {kind: 'howToBecome', label: 'Как стать ИП'};
	}

	return {kind: 'howToBecome', label: 'Как стать самозанятым'};
};

export const getPostInnFlowConfig = (
	workStatus: RegistrationWorkStatus | null,
	options?: PostInnFlowOptions,
): PostInnFlowConfig => {
	const status = workStatus ?? 'self_employment';
	const registrationFlow = options?.registrationFlow ?? FALLBACK_REGISTRATION_FLOW;

	if (options?.selfEmploymentProvider === 'free_smz') {
		return {
			intro: {
				title: 'Подключить?',
				description: FREE_SMZ_INTRO_DESCRIPTION,
				secondaryAction: {kind: 'howToBecome', label: 'Как стать самозанятым'},
				proceedLabel: 'Подключить',
			},
			access: null,
		};
	}

	if (isFinanceSelfEmploymentFlow(workStatus)) {
		return {
			intro: {
				title: 'Приступим?',
				description:
					'Заполните мини-анкету Альфа-Банка. Когда в приложении «Мой налог» поступит запрос на подключение, предоставьте запрошенные права',
				secondaryAction: {kind: 'confirmInfo', label: 'Узнать больше'},
				proceedLabel: 'Продолжить',
				confirm: {
					title: 'Для работы требуется отдельный статус самозанятого',
					description:
						'Для некоторых предложений и заказов нужен статус самозанятого, подключённый через партнёра сервиса. Это требуется даже в том случае, если у вас уже есть статус самозанятого в Рокет Ворк.',
				},
			},
			access: ALFA_MY_TAX_ACCESS,
		};
	}

	return {
		intro: {
			title: 'Подключить?',
			description: buildIntroDescription(status, registrationFlow),
			secondaryAction: buildIntroSecondaryAction(status),
			proceedLabel: 'Подключить',
		},
		access: requiresMyTaxConnectionFlow(status) ? MY_TAX_ACCESS : null,
	};
};
